import React, { useEffect, useRef, useMemo, useImperativeHandle, forwardRef } from "react";
import * as d3 from "d3";
import { Box, Typography } from "@mui/material";
import {
  PortfolioGlidepathResult,
  PortfolioYearEmission,
} from "../Data/PortfolioGlidepathModels";
import { downloadChartImage } from "utilities/downloadChartImage";
import { renderTargetMarkerLines } from "utilities/chartTargetMarkers";
import { formatEmissionValue } from "utilities/utilities";

// COLORS
const COLOR_HISTORICAL = "#5B9BD5";
const COLOR_PROJECTED = "#c5d4e8";
const COLOR_BAU_LINE = "#1a1a2e";
const COLOR_TARGET_LINE = "#f59e0b";
const COLOR_SCOPE1 = "#4472c4";
const COLOR_SCOPE2 = "#70ad47";
const COLOR_SCOPE3 = "#ffc000";
// TYPES
export type PortfolioGlidepathRef = {
  downloadChart: (format: "png" | "jpeg") => void;
};

interface PortfolioGlidepathProps {
  data: PortfolioGlidepathResult;
  width?: number;
  height?: number;
}

// Org/client selections mix locations with different base years, so no single year is "the"
// baseline here - baseline years are drawn as plain historical years. Baseline is still a data
// concept (it anchors the target line), just not a visual one at this level.
type YearClassification = "historical" | "projected";

interface YearPoint {
  year: number;
  residual: number;
  bau: number;
  target?: number;
  classification: YearClassification;
  coveragePercent?: number;
  locationsWithData?: number;
  totalLocations?: number;
  scope1?: number;
  scope2?: number;
  scope3?: number;
}

// LEGEND
const LegendLine = ({ color, dashed }: { color: string; dashed?: boolean }) => (
  <svg width="20" height="12" style={{ flexShrink: 0 }}>
    <line
      x1="0"
      y1="6"
      x2="20"
      y2="6"
      stroke={color}
      strokeWidth="2.5"
      strokeDasharray={dashed ? "4,3" : undefined}
      strokeLinecap="round"
    />
  </svg>
);

const legendChipStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "11.5px",
  color: "#374151",
  fontFamily: "Inter, sans-serif",
  fontWeight: 500,
  backgroundColor: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "20px",
  padding: "4px 12px 4px 8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const getBarColor = (classification: YearClassification) =>
  classification === "historical" ? COLOR_HISTORICAL : COLOR_PROJECTED;

const getEmissionDataLabel = (classification: YearClassification) =>
  classification === "historical" ? "Historical / Actual" : "Projected";

const getResidualLabel = (classification: YearClassification) =>
  classification === "historical"
    ? "Historical Emissions (Actual)"
    : "Projected Residual Emissions";

const PortfolioGlidepath = forwardRef<PortfolioGlidepathRef, PortfolioGlidepathProps>(
  ({ data, width: propWidth, height: propHeight }, ref) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    // The target line is anchored to the earliest baseline year the selection actually has.
    // With no resolvable baseline there is nothing to anchor to, so no target line is drawn -
    // falling back to the first year of the window would silently invent a baseline.
    const flaggedBaselineYears = [
      ...new Set(
        [...data.bauEmissions, ...data.residualEmissions]
          .filter((emission) => emission.isBaseline)
          .map((emission) => emission.year),
      ),
    ];
    const targetBaselineYears =
      data.applicableBaselineYears ?? flaggedBaselineYears;
    const targetStartYear =
      targetBaselineYears.length > 0
        ? Math.min(...targetBaselineYears)
        : null;

    const hasTargetData =
      targetStartYear != null &&
      data.targets != null &&
      data.targets.some((t) => t.value != null && !isNaN(t.value));

    const showTargetCoverage =
      hasTargetData && data.targetCoveragePercent != null;

    // Compose legendItems for download (and for visual legend)
    const targetLegendLabel = showTargetCoverage
      ? `Targeted GHG Emissions (covers ${Math.round(data.targetCoveragePercent!)}% of emissions)`
      : "Targeted GHG Emissions";

    // Scopes excluded by the filter come back as all-zero series and draw no bar segment,
    // so they get no legend swatch either � an entry with nothing to point at only confuses.
    const scopeSeries = data.residualEmissionsByScope ?? [];
    const scopeLegendItems = ([
      { label: "Scope 1", color: COLOR_SCOPE1, read: (s: any) => s.scope1 },
      { label: "Scope 2", color: COLOR_SCOPE2, read: (s: any) => s.scope2 },
      { label: "Scope 3", color: COLOR_SCOPE3, read: (s: any) => s.scope3 },
    ] as const)
      .filter(({ read }) => scopeSeries.some((s) => (read(s) ?? 0) > 0))
      .map(({ label, color }) => ({ label, color, type: "rect" as const }));

    const legendItems: Array<{
      label: string;
      color: string;
      type: "rect" | "solid-line" | "dashed-line";
    }> = [
      { label: "Historical Emissions (Actual)", color: COLOR_HISTORICAL, type: "rect" },
      { label: "Projected Residual Emissions", color: COLOR_PROJECTED, type: "rect" },
      {
        label: "Historical Emissions Trajectory",
        color: COLOR_BAU_LINE,
        type: "solid-line",
      },
      {
        label: "BAU GHG Emissions (Projected)",
        color: COLOR_BAU_LINE,
        type: "dashed-line",
      },
      ...(hasTargetData
        ? [
            {
              label: targetLegendLabel,
              color: COLOR_TARGET_LINE,
              type: "dashed-line" as const,
            },
          ]
        : []),
      ...scopeLegendItems,
    ];

    const handleDownload = (format: "png" | "jpeg") => {
      downloadChartImage({
        svgRef,
        format,
        fileName: `portfolio_target_trajectory.${format}`,
        title: "Portfolio Target Trajectory",
        subtitle: "Residual emissions vs target over time",
        legendItems: legendItems.map((i) => ({
          label: i.label,
          color: i.color,
        })),
        legendConfig: {
          marginLeft: 40,
          marginTop: 60,
          itemWidth: 180,
          itemHeight: 20,
        },
        titlePosition: "bottom",
      });
    };

    useImperativeHandle(ref, () => ({
      downloadChart: handleDownload,
    }));

    // Build points and D3 rendering (same as your logic)
    const yearPoints = useMemo<YearPoint[]>(() => {
      const toValueMap = (arr: PortfolioYearEmission[]) =>
        new Map(arr.map((d) => [d.year, d.value]));
      const toHistoricalMap = (arr: PortfolioYearEmission[]) =>
        new Map(arr.map((d) => [d.year, d.isHistorical]));
      const toBaselineMap = (arr: PortfolioYearEmission[]) =>
        new Map(arr.map((d) => [d.year, d.isBaseline]));

      const bauMap = toValueMap(data.bauEmissions);
      const residualMap = toValueMap(data.residualEmissions);
      const targetMap = data.targets ? toValueMap(data.targets) : undefined;
      const scopeMap = new Map(
        (data.residualEmissionsByScope ?? []).map((s) => [s.year, s]),
      );
      // Prefer BAU's flag, fall back to residual's flag for the same year.
      const bauHistoricalMap = toHistoricalMap(data.bauEmissions);
      const residualHistoricalMap = toHistoricalMap(data.residualEmissions);
      const bauBaselineMap = toBaselineMap(data.bauEmissions);
      const residualBaselineMap = toBaselineMap(data.residualEmissions);
      const coverageMap = new Map(
        (data.dataCoverageByYear ?? []).map((c) => [c.year, c]),
      );

      const isBaselineYear = (yr: number) =>
        bauBaselineMap.get(yr) ?? residualBaselineMap.get(yr) ?? false;

      // A baseline year is actual reported data, so it counts as historical even when the
      // year is not separately flagged that way - it must not fall through to the projected
      // (stacked, dashed) treatment.
      const classify = (yr: number): YearClassification => {
        if (isBaselineYear(yr)) return "historical";
        const isHistorical =
          bauHistoricalMap.get(yr) ?? residualHistoricalMap.get(yr) ?? false;
        return isHistorical ? "historical" : "projected";
      };

      return [...data.years]
        .sort((a, b) => a - b)
        .map<YearPoint>((yr) => {
          const coverage = coverageMap.get(yr);
          const scope = scopeMap.get(yr);
          return {
            year: yr,
            residual: residualMap.get(yr) ?? 0,
            bau: bauMap.get(yr) ?? 0,
            target: targetMap?.get(yr),
            classification: classify(yr),
            coveragePercent: coverage?.coveragePercent,
            locationsWithData: coverage?.locationsWithData,
            totalLocations: coverage?.totalLocations,
            scope1: scope?.scope1,
            scope2: scope?.scope2,
            scope3: scope?.scope3,
          };
        });
    }, [
      data.bauEmissions,
      data.residualEmissions,
      data.targets,
      data.dataCoverageByYear,
      data.years,
      data.residualEmissionsByScope,
    ]);

    useEffect(() => {
      if (data.totalLocationsIncluded === 0) return;

      const margin = { top: 20, right: 50, bottom: 40, left: 75 };
      const width = (propWidth ?? 1100) - margin.left - margin.right;
      const height = (propHeight ?? 420) - margin.top - margin.bottom;

      d3.select(svgRef.current).selectAll("*").remove();

      const svg = d3
        .select(svgRef.current)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const years = yearPoints.map((y) => y.year.toString());

      const x = d3
        .scaleBand<string>()
        .domain(years)
        .range([0, width])
        .padding(0.28);

      const maxValue = Math.max(
        d3.max(yearPoints, (d) => d.bau) ?? 0,
        d3.max(yearPoints, (d) => d.residual) ?? 0,
        d3.max(yearPoints, (d) => d.target ?? 0) ?? 0,
      );

      const y = d3
        .scaleLinear()
        .domain([0, (maxValue || 1) * 1.1])
        .range([height, 0])
        .nice();

      // Horizontal gridlines
      svg
        .append("g")
        .attr("class", "grid")
        .call(
          d3
            .axisLeft(y)
            .tickSize(-width)
            .tickFormat(() => ""),
        )
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".tick line")
            .attr("stroke", "#eaecf0")
            .attr("stroke-dasharray", "4,3"),
        );

      const styleTickText = (
        g: d3.Selection<SVGGElement, unknown, null, undefined>,
      ) =>
        g
          .selectAll(".tick text")
          .attr("fill", "#6b7280")
          .style("font-size", "11px")
          .style("font-family", "Inter, sans-serif");

      // X axis
      svg
        .append("g")
        .attr("transform", `translate(0, ${height})`)
        .call(
          d3.axisBottom(x).tickValues(x.domain().filter((_, i) => i % 2 === 0)),
        )
        .call((g) => g.select(".domain").attr("stroke", "#d1d5db"))
        .call((g) => g.selectAll(".tick line").attr("stroke", "#d1d5db"))
        .call((g) => styleTickText(g));

      // Y axis
      svg
        .append("g")
        .call(d3.axisLeft(y).tickFormat(d3.format("~s")).ticks(6))
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll(".tick line").remove())
        .call((g) => styleTickText(g).attr("dx", "-4"));

      // Y axis label
      svg
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", 0 - margin.left)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font-size", "12px")
        .style("font-family", "Inter, sans-serif")
        .style("font-weight", "500")
        .style("fill", "#6b7280")
        .text("Annual Emissions (tCO2e)");

      const tooltip = d3.select(tooltipRef.current);

      const showTooltip = (_event: MouseEvent, d: YearPoint) => {
        const barColor = getBarColor(d.classification);
        const isHistorical = d.classification === "historical";

        let tooltipContent = `
          <div style="border-left: 4px solid ${barColor}; padding-left: 10px; margin-bottom: 6px;">
            <div style="font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 3px;">${d.year}</div>
            <div style="color: #9ca3af; font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600;">
              ${getEmissionDataLabel(d.classification)}
            </div>
          </div>
          <div style="height: 1px; background: #f3f4f6; margin: 8px 0;"></div>
          ${d.classification === "projected" && (d.scope1 != null || d.scope2 != null || d.scope3 != null)
            ? `${[
                { label: "Scope 1", value: d.scope1 ?? 0, color: COLOR_SCOPE1 },
                { label: "Scope 2", value: d.scope2 ?? 0, color: COLOR_SCOPE2 },
                { label: "Scope 3", value: d.scope3 ?? 0, color: COLOR_SCOPE3 },
              ]
                // A scope excluded by the filter arrives as zero. Listing it would read as a
                // measured nil rather than "not asked for", so it is left out entirely.
                .filter((s) => s.value !== 0)
                .map(
                  (s, i) => `<div style="display: flex; align-items: center; gap: 8px; margin-top: ${i === 0 ? 8 : 6}px;">
                <span style="width: 10px; height: 10px; border-radius: 2px; background: ${s.color}; display: inline-block; flex-shrink: 0;"></span>
                <span style="color: #6b7280; font-size: 12px;">${s.label}</span>
                <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(s.value)}</span>
              </div>`
                )
                .join("")}
              <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px; border-top: 1px solid #f3f4f6; padding-top: 6px;">
                <span style="color: #6b7280; font-size: 12px; font-weight: 600;">Total Residual</span>
                <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(d.residual)}</span>
              </div>`
            : `<div style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
                <span style="width: 10px; height: 10px; border-radius: 2px; background: ${barColor}; display: inline-block; flex-shrink: 0;"></span>
                <span style="color: #6b7280; font-size: 12px;">${getResidualLabel(d.classification)}</span>
                <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(d.residual)}</span>
              </div>`
          }
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
            <span style="width: 10px; height: 2px; background: ${COLOR_BAU_LINE}; display: inline-block; flex-shrink: 0;${isHistorical ? "" : " border-top: 2px dashed " + COLOR_BAU_LINE + ";"}"></span>
            <span style="color: #6b7280; font-size: 12px;">${isHistorical
              ? "Historical Emissions Trajectory"
              : "BAU GHG Emissions"}</span>
            <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(d.bau)}</span>
          </div>`;

        if (hasTargetData && d.target != null && !isNaN(d.target)) {
          tooltipContent += `
            <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
              <span style="width: 10px; height: 2px; display: inline-block; flex-shrink: 0; border-top: 2px dashed ${COLOR_TARGET_LINE};"></span>
              <span style="color: #6b7280; font-size: 12px;">Targeted GHG Emissions</span>
              <span style="margin-left: auto; font-weight: 700; color: #111827; font-size: 13px;">${formatEmissionValue(d.target)}</span>
            </div>`;
        }

        if (
          d.coveragePercent != null &&
          d.totalLocations != null &&
          d.locationsWithData != null
        ) {
          tooltipContent += `
          <div style="height: 1px; background: #f3f4f6; margin: 8px 0;"></div>
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
            <span style="color: #9ca3af; font-size: 11px;">Data coverage: ${Math.round(d.coveragePercent)}% (${d.locationsWithData} of ${d.totalLocations} locations)</span>
          </div>`;
        }

        const barRightX =
          margin.left + (x(d.year.toString()) ?? 0) + x.bandwidth() + 12;
        const barTopY = margin.top + y(d.bau);

        tooltip
          .style("opacity", 1)
          .html(tooltipContent)
          .style("left", `${barRightX}px`)
          .style("top", `${barTopY}px`);
      };

      const hideTooltip = () => {
        tooltip.style("opacity", 0);
      };

      const historicalPoints = yearPoints.filter((d) => d.classification === "historical");

      svg
        .selectAll(".bar")
        .data(historicalPoints)
        .enter()
        .append("rect")
        .attr("class", "bar")
        .attr("x", (d) => x(d.year.toString())!)
        .attr("y", height)
        .attr("width", x.bandwidth())
        .attr("height", 0)
        .attr("rx", 3)
        .attr("ry", 3)
        .attr("fill", COLOR_HISTORICAL)
        .on("mouseover", (event, d) => showTooltip(event, d))
        .on("mousemove", (event, d) => showTooltip(event, d))
        .on("mouseout", hideTooltip)
        .transition()
        .duration(700)
        .ease(d3.easeCubicOut)
        .attr("y", (d) => y(d.residual))
        .attr("height", (d) => Math.max(0, height - y(d.residual)));

      type ScopeSegment = {
        point: YearPoint;
        scope: 1 | 2 | 3;
        y0: number;
        y1: number;
        color: string;
      };

      const projectedPoints = yearPoints.filter((d) => d.classification === "projected");

      const scopeSegments: ScopeSegment[] = projectedPoints.flatMap((d) => {
        const hasScopeData =
          d.scope1 != null || d.scope2 != null || d.scope3 != null;

        if (!hasScopeData) {
          return [
            { point: d, scope: 3 as const, y0: 0, y1: d.residual, color: COLOR_PROJECTED },
          ];
        }

        const s1 = d.scope1 ?? 0;
        const s2 = d.scope2 ?? 0;
        const s3 = d.scope3 ?? 0;

        return [
          { point: d, scope: 1 as const, y0: 0, y1: s1, color: COLOR_SCOPE1 },
          { point: d, scope: 2 as const, y0: s1, y1: s1 + s2, color: COLOR_SCOPE2 },
          { point: d, scope: 3 as const, y0: s1 + s2, y1: s1 + s2 + s3, color: COLOR_SCOPE3 },
        ].filter((seg) => seg.y1 > seg.y0);
      });

      svg
        .selectAll(".scope-bar")
        .data(scopeSegments)
        .enter()
        .append("rect")
        .attr("class", "scope-bar")
        .attr("x", (d) => x(d.point.year.toString())!)
        .attr("y", height)
        .attr("width", x.bandwidth())
        .attr("height", 0)
        .attr("rx", 0)
        .attr("ry", 0)
        .attr("fill", (d) => d.color)
        .attr("fill-opacity", 0.4)
        .on("mouseover", (event, d) => showTooltip(event, d.point))
        .on("mousemove", (event, d) => showTooltip(event, d.point))
        .on("mouseout", hideTooltip)
        .transition()
        .duration(700)
        .ease(d3.easeCubicOut)
        .attr("y", (d) => y(d.y1))
        .attr("height", (d) => Math.max(0, y(d.y0) - y(d.y1)));

      // BAU line segments (solid/historic, dashed/projected)
      type LineSegment = { data: YearPoint[]; isSolid: boolean };
      const bauLine = d3
        .line<YearPoint>()
        .x((d) => x(d.year.toString())! + x.bandwidth() / 2)
        .y((d) => y(d.bau))
        .curve(d3.curveMonotoneX);

      const segments: LineSegment[] = [];
      if (yearPoints.length > 0) {
        const isSolidStroke = (p: YearPoint) => p.classification !== "projected";
        let currentData: YearPoint[] = [yearPoints[0]];
        let currentIsSolid = isSolidStroke(yearPoints[0]);

        for (let i = 1; i < yearPoints.length; i++) {
          const point = yearPoints[i];
          const pointIsSolid = isSolidStroke(point);
          if (pointIsSolid === currentIsSolid) {
            currentData.push(point);
          } else {
            segments.push({ data: [...currentData], isSolid: currentIsSolid });
            currentData = [yearPoints[i - 1], point];
            currentIsSolid = pointIsSolid;
          }
        }
        segments.push({ data: currentData, isSolid: currentIsSolid });
      }

      segments.forEach((segment) => {
        svg
          .append("path")
          .datum(segment.data)
          .attr("fill", "none")
          .attr("stroke", COLOR_BAU_LINE)
          .attr("stroke-width", segment.isSolid ? 3 : 2.5)
          .attr("stroke-dasharray", segment.isSolid ? "none" : "8,5")
          .attr("stroke-linecap", "round")
          .attr("d", bauLine);
      });

      // BAU dots
      svg
        .selectAll(".bau-dot")
        .data(yearPoints)
        .enter()
        .append("circle")
        .attr("class", "bau-dot")
        .attr("cx", (d) => x(d.year.toString())! + x.bandwidth() / 2)
        .attr("cy", (d) => y(d.bau))
        .attr("r", 4)
        .attr("fill", "#fff")
        .attr("stroke", COLOR_BAU_LINE)
        .attr("stroke-width", 2)
        .on("mouseover", (event, d) => showTooltip(event, d))
        .on("mousemove", (event, d) => showTooltip(event, d))
        .on("mouseout", hideTooltip);

      // Target line + dots
      if (hasTargetData) {
        type TargetPoint = YearPoint & { target: number };
        const targetPoints = yearPoints
          .filter((d) => d.year >= targetStartYear)
          .filter((d): d is TargetPoint => d.target != null && !isNaN(d.target));

        const targetLine = d3
          .line<TargetPoint>()
          .x((d) => x(d.year.toString())! + x.bandwidth() / 2)
          .y((d) => y(d.target))
          .curve(d3.curveMonotoneX);

        svg
          .append("path")
          .datum(targetPoints)
          .attr("fill", "none")
          .attr("stroke", COLOR_TARGET_LINE)
          .attr("stroke-width", 2.5)
          .attr("stroke-dasharray", "7,5")
          .attr("stroke-linecap", "round")
          .attr("d", targetLine);

        svg
          .selectAll(".target-dot")
          .data(targetPoints)
          .enter()
          .append("circle")
          .attr("class", "target-dot")
          .attr("cx", (d) => x(d.year.toString())! + x.bandwidth() / 2)
          .attr("cy", (d) => y(d.target))
          .attr("r", 4)
          .attr("fill", "#fff")
          .attr("stroke", COLOR_TARGET_LINE)
          .attr("stroke-width", 2)
          .on("mouseover", (event, d) => showTooltip(event, d))
          .on("mousemove", (event, d) => showTooltip(event, d))
          .on("mouseout", hideTooltip);
      }

      // Build marker map then delegate rendering to shared utility
      if (data.targetMarkers && data.targetMarkers.length > 0) {
        const markersByYear = new Map<number, string[]>();
        data.targetMarkers.forEach((m) => {
          const names = m.targets.map((t) => t.targetName).filter(Boolean);
          if (names.length > 0) markersByYear.set(m.year, names);
        });
        renderTargetMarkerLines(svg, markersByYear, x, height);
      }
    }, [data, yearPoints, hasTargetData, targetStartYear, propWidth, propHeight]);

    if (data.totalLocationsIncluded === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 320,
            color: "#64748b",
            fontSize: "0.9rem",
          }}
        >
          <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
            No location data available for the selected filters.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", position: "relative" }}>
        {/* SVG Chart */}
        <div style={{ padding: "0 6px", overflowX: "auto" }}>
          <svg ref={svgRef} width="100%" height={(propHeight ?? 420) + 60} />
        </div>
        {/* Legend */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px 10px",
            padding: "14px 24px 20px",
            borderTop: "1px solid #f0f2f6",
            backgroundColor: "#f8fafc",
            borderRadius: "0 0 12px 12px",
          }}
        >
          {legendItems.map((item) => (
            <div key={`${item.label}-${item.color}-${item.type}`} style={legendChipStyle}>
              {item.type === "rect" && (
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 2,
                    backgroundColor: item.color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
              )}
              {item.type === "solid-line" && <LegendLine color={item.color} />}
              {item.type === "dashed-line" && <LegendLine color={item.color} dashed />}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        {/* Tooltip */}
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            background: "#ffffff",
            padding: "14px 16px",
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            pointerEvents: "none",
            opacity: 0,
            fontSize: "13px",
            fontFamily: "Inter, sans-serif",
            boxShadow:
              "0 10px 25px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            minWidth: "240px",
            transition: "opacity 0.15s ease",
            zIndex: 1000,
          }}
        />
      </Box>
    );
  }
);

export default PortfolioGlidepath;